import { useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  Phone,
  Users,
  MessageSquare,
  Mail,
  Coffee,
  UtensilsCrossed,
  Video,
  MoreHorizontal,
  Search,
  Clock,
  X,
  ClipboardList,
  Activity,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDashboard, useSnoozeFollowUp, useDismissFollowUp } from '@/hooks/use-dashboard'
import { useCreateInteraction } from '@/hooks/use-interactions'
import { FollowUpSuggestionsWidget } from '@/components/FollowUpSuggestionsWidget'
import { WeeklyGoalCard } from '@/components/WeeklyGoalCard'
import { HealthDashboardCard } from '@/components/HealthDashboardCard'
import { ContactUpdatesWidget } from '@/components/ContactUpdatesWidget'
import { UpcomingEventsWidget } from '@/components/UpcomingEventsWidget'
import { AimeLogo } from '@/components/AimeLogo'

const typeIcons: Record<string, typeof Phone> = {
  call: Phone,
  meeting: Users,
  text: MessageSquare,
  email: Mail,
  coffee: Coffee,
  dinner: UtensilsCrossed,
  video_call: Video,
  other: MoreHorizontal,
}

export default function HomePage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isLoading, refetch } = useDashboard()
  const snoozeFollowUp = useSnoozeFollowUp()
  const dismissFollowUp = useDismissFollowUp()
  const createInteraction = useCreateInteraction()
  const [refreshing, setRefreshing] = useState(false)
  const [loggingContactId, setLoggingContactId] = useState<string | null>(null)
  const touchStartY = useRef(0)
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useCallback(
    async (e: React.TouchEvent) => {
      const scrollTop = scrollRef.current?.scrollTop ?? 0
      const deltaY = e.changedTouches[0].clientY - touchStartY.current
      if (scrollTop <= 0 && deltaY > 80 && !refreshing) {
        setRefreshing(true)
        await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        await refetch()
        setRefreshing(false)
      }
    },
    [refreshing, queryClient, refetch],
  )

  function handleQuickLog(contactId: string, contactName: string) {
    setLoggingContactId(contactId)
    createInteraction.mutate(
      {
        contact_id: contactId,
        type: 'other',
        title: `Reached out to ${contactName}`,
        occurred_at: new Date().toISOString(),
      },
      {
        onSettled: () => {
          setLoggingContactId(null)
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div className="bg-muted h-8 w-32 animate-pulse rounded-lg" />
          <div className="bg-muted h-8 w-8 animate-pulse rounded-full" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-surface-container-low h-24 animate-pulse rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-surface-container-high h-40 animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  const todayReminders = data?.todayReminders ?? []
  const overdueContacts = data?.overdueContacts ?? []
  const recentInteractions = data?.recentInteractions ?? []
  const stats = data?.stats

  return (
    <div
      ref={scrollRef}
      className="relative flex flex-1 flex-col gap-8 overflow-y-auto px-6 pt-6 pb-8"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {refreshing && (
        <div className="flex justify-center pb-2">
          <div className="border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      )}

      {/* Top bar with brand name and search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AimeLogo size={40} />
        </div>
        <Link to="/search" className="text-muted-foreground hover:text-accent transition-colors">
          <Search className="h-5 w-5" />
        </Link>
      </div>

      {/* Stats Hero */}
      {stats && (
        <section className="grid grid-cols-3 gap-4">
          <div className="bg-surface-container-low flex flex-col items-center justify-center rounded-xl p-5 text-center">
            <span className="text-on-surface-variant mb-1 text-[10px] tracking-widest uppercase">
              {t('home.contacts', 'Contacts')}
            </span>
            <span className="font-heading text-primary text-3xl font-extrabold">
              {stats.totalContacts}
            </span>
          </div>
          <div className="bg-surface-container-low flex flex-col items-center justify-center rounded-xl p-5 text-center">
            <span className="text-on-surface-variant mb-1 text-[10px] tracking-widest uppercase">
              {t('home.dueToday', 'Due Today')}
            </span>
            <span className="font-heading text-ring text-3xl font-extrabold">
              {stats.remindersToday}
            </span>
          </div>
          <div className="bg-surface-container-low flex flex-col items-center justify-center rounded-xl p-5 text-center">
            <span className="text-on-surface-variant mb-1 text-[10px] tracking-widest uppercase">
              {t('home.overdue', 'Overdue')}
            </span>
            <span
              className={cn(
                'font-heading text-3xl font-extrabold',
                stats.overdueCount > 0 ? 'text-error' : 'text-foreground',
              )}
            >
              {String(stats.overdueCount).padStart(2, '0')}
            </span>
          </div>
        </section>
      )}

      {/* Weekly Goal */}
      {stats && stats.totalContacts > 0 && <WeeklyGoalCard />}

      {/* Upcoming events (saints, name days, birthdays, reminders) */}
      <UpcomingEventsWidget />

      {/* Relationship Health */}
      {data?.healthSummary && (stats?.totalContacts ?? 0) > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Activity className="text-primary h-4 w-4" />
            <h2 className="font-heading text-base font-bold">
              {t('health.title', 'Relationship Health')}
            </h2>
          </div>
          <div className="bg-surface-container-low rounded-xl p-4">
            {/* Health bar */}
            <div className="mb-3 flex h-3 overflow-hidden rounded-full">
              {data.healthSummary.healthy > 0 && (
                <div
                  className="bg-chartreuse transition-all"
                  style={{
                    width: `${(data.healthSummary.healthy / stats!.totalContacts) * 100}%`,
                  }}
                />
              )}
              {data.healthSummary.fading > 0 && (
                <div
                  className="bg-coral/60 transition-all"
                  style={{
                    width: `${(data.healthSummary.fading / stats!.totalContacts) * 100}%`,
                  }}
                />
              )}
              {data.healthSummary.neglected > 0 && (
                <div
                  className="bg-coral transition-all"
                  style={{
                    width: `${(data.healthSummary.neglected / stats!.totalContacts) * 100}%`,
                  }}
                />
              )}
              {data.healthSummary.newContacts > 0 && (
                <div
                  className="bg-lilac transition-all"
                  style={{
                    width: `${(data.healthSummary.newContacts / stats!.totalContacts) * 100}%`,
                  }}
                />
              )}
            </div>
            {/* Legend */}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <span className="text-chartreuse-foreground dark:text-chartreuse text-lg font-bold">
                  {data.healthSummary.healthy}
                </span>
                <p className="text-on-surface-variant text-[10px]">
                  {t('health.healthy', 'Healthy')}
                </p>
              </div>
              <div>
                <span className="text-coral text-lg font-bold">{data.healthSummary.fading}</span>
                <p className="text-on-surface-variant text-[10px]">
                  {t('health.fading', 'Fading')}
                </p>
              </div>
              <div>
                <span className="text-coral text-lg font-bold">{data.healthSummary.neglected}</span>
                <p className="text-on-surface-variant text-[10px]">
                  {t('health.neglected', 'Neglected')}
                </p>
              </div>
              <div>
                <span className="text-on-surface-variant text-lg font-bold">
                  {data.healthSummary.newContacts}
                </span>
                <p className="text-on-surface-variant text-[10px]">{t('health.new', 'New')}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Smart Follow-up Suggestions */}
      <FollowUpSuggestionsWidget />
      {/* Contact Updates */}
      {data?.contactUpdates && <ContactUpdatesWidget data={data.contactUpdates} />}
      {/* Health Dashboard (gamification) */}
      {stats && stats.totalContacts > 0 && <HealthDashboardCard />}

      {/* Overdue Follow-ups */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-base font-bold">
            {t('followUp.title', 'Overdue Follow-ups')}
          </h2>
          {overdueContacts.length > 0 && (
            <span className="bg-error/10 text-error rounded-full px-2.5 py-0.5 text-xs font-semibold">
              {overdueContacts.length}
            </span>
          )}
        </div>
        {overdueContacts.length === 0 ? (
          <div className="bg-surface-container-low rounded-xl p-4">
            <p className="text-on-surface-variant text-sm">{t('home.noOverdue')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {overdueContacts.map((contact) => {
              const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
              const initials = [contact.firstName?.[0], contact.lastName?.[0]]
                .filter(Boolean)
                .join('')
                .toUpperCase()
              const isLogging = loggingContactId === contact.id

              return (
                <div
                  key={contact.id}
                  className="bg-surface-container-low overflow-hidden rounded-xl"
                >
                  {/* Contact info row */}
                  <Link
                    to={`/contacts/${contact.id}`}
                    className="hover:bg-surface-container-high flex items-center gap-3 p-4 transition-colors"
                  >
                    {contact.avatarUrl ? (
                      <img
                        src={contact.avatarUrl}
                        alt={name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold">
                        {initials || '?'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{name || 'Unknown'}</p>
                      <p className="text-on-surface-variant text-[11px]">
                        {contact.lastContactedAt
                          ? t('followUp.lastContact', 'Last: {{time}}', {
                              time: formatDistanceToNow(new Date(contact.lastContactedAt), {
                                addSuffix: true,
                              }),
                            })
                          : t('home.neverContacted', 'Never contacted')}
                        {contact.daysOverdue != null && contact.daysOverdue > 0 && (
                          <span className="text-error ml-1 font-medium">
                            {' '}
                            (
                            {t('followUp.daysOverdue', '{{count}}d overdue', {
                              count: contact.daysOverdue,
                            })}
                            )
                          </span>
                        )}
                      </p>
                    </div>
                    <ChevronRight className="text-outline h-4 w-4 shrink-0" />
                  </Link>

                  {/* Quick actions bar */}
                  <div className="border-outline-variant/10 flex border-t">
                    <button
                      onClick={() => handleQuickLog(contact.id, name)}
                      disabled={isLogging}
                      className="text-on-surface-variant hover:bg-surface-container-high hover:text-primary flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors disabled:opacity-50"
                    >
                      <ClipboardList className="h-3.5 w-3.5" />
                      {t('followUp.log', 'Log')}
                    </button>
                    <div className="bg-outline-variant/20 w-px" />
                    <button
                      onClick={() => snoozeFollowUp.mutate(contact.id)}
                      className="text-on-surface-variant hover:bg-surface-container-high hover:text-primary flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors"
                    >
                      <Clock className="h-3.5 w-3.5" />
                      {t('followUp.snooze', 'Snooze')}
                    </button>
                    <div className="bg-outline-variant/20 w-px" />
                    <button
                      onClick={() => dismissFollowUp.mutate(contact.id)}
                      className="text-on-surface-variant hover:bg-surface-container-high hover:text-error flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[11px] font-medium transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('followUp.dismiss', 'Dismiss')}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Today's Reminders */}
      {todayReminders.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-heading text-base font-bold">{t('home.today')}</h2>
          <div className="bg-surface-container-low overflow-hidden rounded-xl">
            {todayReminders.map((reminder, index) => (
              <Link
                key={reminder.id}
                to="/reminders"
                className={cn(
                  'hover:bg-surface-container-high flex items-center gap-4 p-4 transition-colors',
                  index > 0 && 'border-outline-variant/20 border-t',
                )}
              >
                <div className="bg-lilac-soft text-lilac-foreground dark:text-lilac flex h-8 w-8 items-center justify-center rounded-full">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{reminder.title}</p>
                  {reminder.contactName && (
                    <p className="text-on-surface-variant text-[10px]">{reminder.contactName}</p>
                  )}
                </div>
                <ChevronRight className="text-outline h-4 w-4" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recent Activity */}
      <section className="space-y-4">
        <h2 className="font-heading text-base font-bold">{t('home.recentActivity')}</h2>
        {recentInteractions.length === 0 ? (
          <div className="bg-surface-container-low rounded-xl p-4">
            <p className="text-on-surface-variant text-sm">{t('home.noActivity')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {recentInteractions.map((interaction) => {
              const Icon = typeIcons[interaction.type] ?? MoreHorizontal
              return (
                <Link
                  key={interaction.id}
                  to={`/contacts/${interaction.contactId}`}
                  className="hover:bg-surface-container-low group flex items-start gap-4 rounded-xl p-4 transition-colors"
                >
                  <div className="mt-0.5">
                    <Icon className="text-primary h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      <span className="font-bold">
                        {interaction.title || interaction.type.replace('_', ' ')}
                      </span>
                      {interaction.contactName && ` — ${interaction.contactName}`}
                    </p>
                    <p className="text-on-surface-variant mt-1 text-xs">
                      {formatDistanceToNow(new Date(interaction.occurredAt), { addSuffix: true })}
                    </p>
                  </div>
                  <ChevronRight className="text-outline group-hover:text-primary mt-1 h-4 w-4 transition-colors" />
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
